import { QuotaNotice } from "@/components/billing/quota-notice";
import { AccessBadge } from "@/components/app/access-badge";
import { useMemo, useState } from "react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import {
  BookOpen,
  CalendarCheck,
  CalendarDays,
  CheckCheck,
  Info,
  ListTodo,
  Plus,
  Search,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrettyDate } from "@/lib/app-types";
import { RippleButton } from "@/components/app/ripple-button";
import { RevisionPlanCard } from "@/components/app/revision-plan-card";
import { ChapterCard } from "@/components/app/chapter-card";
import { ChapterDetailSheet } from "@/components/app/chapter-detail-sheet";
import { StudyCalendar } from "@/components/app/study-calendar";
import { RevisionInfoSheet } from "@/components/app/revision-info-sheet";
import { REVISION_LEGEND } from "@/lib/revision-labels";
import { useStudyActions } from "@/components/app/app-shell";
import { useChapters } from "@/hooks/use-chapters";
import { buildDailyPlan, MAX_DAILY_REVISIONS } from "@/lib/lecture-plan";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/revisions")({
  head: () => ({
    ...seo({
      title: "Revision Planner — The Quantum Minds",
      description:
        "A balanced daily lecture revision plan on the 1-3-7-15-30-60-75 day spaced repetition schedule for JEE & NEET.",
      path: "/revisions",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Revision Planner", path: "/revisions" },
      ]),
    ],
  }),
  component: RevisionsPage,
});

const TABS = [
  { key: "plan", label: "Today", icon: ListTodo },
  { key: "upcoming", label: "Upcoming", icon: CalendarCheck },
  { key: "chapters", label: "Chapters", icon: BookOpen },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
] as const;

type Tab = (typeof TABS)[number]["key"];

function RevisionsPage() {
  const api = useChapters();
  const { chapters, today, isPending } = api;
  const [tab, setTab] = useState<Tab>("plan");
  const { openAddChapter } = useStudyActions();
  const [search, setSearch] = useState("");
  // The explainer is opt-in only — it never opens by itself.
  const [infoOpen, setInfoOpen] = useState(false);

  const filteredChapters = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chapters;
    return chapters.filter(
      (c) => c.chapter.toLowerCase().includes(q) || c.subject.toLowerCase().includes(q),
    );
  }, [chapters, search]);

  const plan = useMemo(() => buildDailyPlan(chapters, today), [chapters, today]);
  const planCount = plan.reduce((sum, g) => sum + g.revisions.length, 0);
  const missedCount = chapters.reduce(
    (sum, c) => sum + c.revisions.filter((r) => r.status === "pending" && r.dueDate < today).length,
    0,
  );
  const pendingTotal = chapters.reduce(
    (sum, c) => sum + c.revisions.filter((r) => r.status === "pending").length,
    0,
  );
  const doneTotal = chapters.reduce(
    (sum, c) => sum + c.revisions.filter((r) => r.status === "completed").length,
    0,
  );

  const upcoming = useMemo(() => {
    const byDate = new Map<string, { subject: string; chapter: string; count: number }[]>();
    for (const chapter of chapters) {
      const counts = new Map<string, number>();
      for (const r of chapter.revisions) {
        if (r.status !== "pending" || r.dueDate <= today) continue;
        counts.set(r.dueDate, (counts.get(r.dueDate) ?? 0) + 1);
      }
      for (const [date, count] of counts) {
        const list = byDate.get(date) ?? [];
        list.push({ subject: chapter.subject, chapter: chapter.chapter, count });
        byDate.set(date, list);
      }
    }
    return Array.from(byDate.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(0, 14);
  }, [chapters, today]);

  return (
    <div className="mx-auto min-h-screen w-full max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
      <motion.header
        initial={{ y: -14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45 }}
      >
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-extrabold tracking-tight">
            Revision Planner <AccessBadge level="PRO + FREE" />
          </h1>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            aria-label="How revision works"
            className="flex size-11 shrink-0 items-center justify-center rounded-full border border-hairline bg-surface text-muted-foreground"
          >
            <Info className="size-4" />
          </button>
          <ShareButton className="ml-auto" />
        </div>

        <p className="mt-1 text-sm text-muted-foreground">
          Balanced across subjects · max {MAX_DAILY_REVISIONS} lecture revisions a day
        </p>
        <RippleButton className="mt-4 w-full" onClick={openAddChapter}>
          <Plus className="size-4" /> Add chapter
        </RippleButton>
      </motion.header>

      <motion.section
        initial={{ y: 18, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 130, damping: 20 }}
        className="glass mt-5 grid grid-cols-3 gap-2 rounded-[24px] p-4 text-center"
      >
        {[
          { label: "Today", value: planCount, tone: "text-primary" },
          { label: "Missed", value: missedCount, tone: "text-warning" },
          { label: "Completed", value: doneTotal, tone: "text-success" },
        ].map((stat) => (
          <div key={stat.label}>
            <p className={cn("text-xl font-extrabold tabular-nums", stat.tone)}>{stat.value}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {stat.label}
            </p>
          </div>
        ))}
      </motion.section>

      <div className="glass mt-4 flex gap-1 rounded-full p-1">
        {TABS.map((item) => {
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              onClick={() => setTab(item.key)}
              className="relative flex min-h-[44px] min-w-0 flex-1 items-center justify-center gap-1 rounded-full px-1.5 py-2 text-[11px] font-semibold tracking-normal"
            >
              {active ? (
                <motion.span
                  layoutId="rev-tab"
                  transition={{ type: "spring", stiffness: 320, damping: 28 }}
                  className="absolute inset-0 rounded-full [background-image:var(--gradient-brand)]"
                />
              ) : null}
              <item.icon
                className={cn(
                  "relative size-3.5 shrink-0",
                  active ? "text-primary-foreground" : "text-muted-foreground",
                )}
              />
              <span
                className={cn(
                  "relative whitespace-nowrap",
                  active ? "text-primary-foreground" : "text-muted-foreground",
                )}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      <QuotaNotice message={api.error} className="mt-4" />

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="mt-5"
        >
          {isPending ? (
             <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="glass animate-shimmer h-28 overflow-hidden rounded-[24px]"
                />
              ))}
            </div>
          ) : tab === "plan" ? (
            plan.length === 0 ? (
              <EmptyState
                title="Nothing to revise today"
                subtitle="Mark more lectures complete and their revisions will appear here automatically."
              />
            ) : (
              <div className="space-y-3">
                <div className="glass flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-2xl px-4 py-2.5 text-[11.5px] text-muted-foreground">
                  {REVISION_LEGEND.filter((item) => item.label !== "Carried").map((item) => (
                    <span key={item.label} className="flex items-center gap-1.5">
                      <span className={cn("size-2 rounded-full", item.dot)} />
                      {item.label}
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {plan.map((group, i) => (
                    <RevisionPlanCard
                      key={group.chapterId}
                      group={group}
                      index={i}
                      today={today}
                      busy={api.busy}
                      onOpen={api.openDetail}
                      onCompleteAll={api.completeRevisions}
                    />
                  ))}
                </div>

                <RippleButton
                  variant="glass"
                  className="w-full"
                  disabled={api.busy}
                  onClick={() =>
                    api.completeRevisions(plan.flatMap((g) => g.revisions.map((r) => r.id)))
                  }
                >
                  <CheckCheck className="size-4" /> Mark all {planCount} revised
                </RippleButton>
              </div>
            )
          ) : tab === "chapters" ? (
            <div className="space-y-3">
              <div className="glass flex items-center gap-2 rounded-full px-4 py-2.5">
                <Search className="size-4 shrink-0 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search chapters"
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
              {filteredChapters.length === 0 ? (
                <EmptyState
                  title={chapters.length === 0 ? "No chapters yet" : "No matches"}
                  subtitle={
                    chapters.length === 0
                      ? "Add a chapter, choose its lectures and tick them off whenever you finish."
                      : "Try a different chapter or subject name."
                  }
                />
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredChapters.map((chapter, i) => (
                    <ChapterCard
                      key={chapter.id}
                      chapter={chapter}
                      today={today}
                      index={i}
                      onOpen={(c) => api.openDetail(c.id)}
                    />
                  ))}
                </div>
              )}

            </div>
          ) : tab === "upcoming" ? (
            upcoming.length === 0 ? (
              <EmptyState
                title="No upcoming revisions"
                subtitle={`${pendingTotal} pending revisions in total.`}
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {upcoming.map(([date, items], i) => (
                  <motion.div
                    key={date}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.04, 0.3) }}
                    className="glass rounded-[24px] p-4"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[13px] font-bold tracking-tight">
                        {formatPrettyDate(date)}
                      </p>
                      <span className="rounded-full bg-primary/12 px-2 py-0.5 text-[11px] font-semibold text-primary">
                        {items.reduce((s, it) => s + it.count, 0)} lectures
                      </span>
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {items.map((item) => (
                        <div
                          key={`${item.subject}-${item.chapter}`}
                          className="flex items-center gap-2 rounded-2xl border border-hairline bg-surface px-3 py-2 text-[12px]"
                        >
                          <span className="min-w-0 flex-1 truncate font-semibold">
                            {item.chapter}
                          </span>
                          <span className="text-muted-foreground">
                            {item.subject} · {item.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            )
          ) : (
            <StudyCalendar chapters={chapters} today={today} />
          )}
        </motion.div>
      </AnimatePresence>

      <ChapterDetailSheet
        chapter={api.detail}
        today={today}
        busy={api.busy}
        onClose={api.closeDetail}
        onMarkLecture={api.markLecture}
        onCompleteRevisions={api.completeRevisions}
        onUpdateLectures={(total) =>
          api.detail ? api.setLectureCount(api.detail.id, total) : undefined
        }
        onDelete={() => (api.detail ? api.deleteChapter(api.detail.id) : undefined)}
      />

      <RevisionInfoSheet
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
      />
    </div>
  );
}

function EmptyState({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="glass rounded-[26px] p-8 text-center">
      <Sparkles className="mx-auto size-8 text-primary" />
      <p className="mt-4 font-semibold">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}
